<script>
import {defineComponent, watch, ref, onMounted, onUnmounted} from 'vue'
import {useCartStore} from "@js/store/CartStore.js";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import {checkDocument} from "@js/helpers.ts";

export default defineComponent({
    name: "CartDelivery",
    setup() {
        const cartStore = useCartStore();
        const query = ref(cartStore.form.delivery_address);
        const suggestions = ref([]);
        const dropdownRef = ref(null);

        let debounce = null;

        const fetchSuggestions = () => {
            clearTimeout(debounce);
            debounce = setTimeout(async () => {
                if ((query.value || '').length < 3) {
                    suggestions.value = [];
                    return;
                }
                suggestions.value = await getAddressSuggestions(query.value);
            }, 300);
        };

        const selectSuggestion = (value) => {
            query.value = value;
            cartStore.form.delivery_address = value;
            suggestions.value = [];
        };

        watch(query, (val) => {
            cartStore.form.delivery_address = val;
        });

        const handleClickOutside = (event) => {
            if (!dropdownRef.value?.contains(event.target)) {
                suggestions.value = [];
            }
        };

        onMounted(() => {
            if (checkDocument()) {
                document.addEventListener('click', handleClickOutside);
            }
        });

        onUnmounted(() => {
            if (checkDocument()) {
                document.removeEventListener('click', handleClickOutside);
            }
        });

        return {
            cartStore,
            query,
            suggestions,
            fetchSuggestions,
            selectSuggestion,
            dropdownRef,
        };
    }
})
</script>

<template>
    <div class="bg-grayBg p-5 md:p-8 rounded-2xl space-y-6">
        <div>
            <h2 class="text-xl font-medium mb-3">Как хотите сдать лом</h2>

            <div class="space-y-4">
                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="[
                        cartStore.form.delivery_type === 'self_delivery'
                            ? 'border-baseColor border-solid'
                            : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                    ]"
                >
                    <input
                        v-model="cartStore.form.delivery_type"
                        type="radio"
                        value="self_delivery"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <div>
                        <div class="font-medium">Самостоятельно привезу на приёмку</div>
                        <div class="text-sm text-gray-500">
                            Подходит, если вы сами доставляете лом на площадку и хотите заранее согласовать условия приёма.
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="[
                        cartStore.form.delivery_type === 'pickup_moscow'
                            ? 'border-baseColor border-solid'
                            : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                    ]"
                >
                    <input
                        v-model="cartStore.form.delivery_type"
                        type="radio"
                        value="pickup_moscow"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <div class="flex justify-between w-full">
                        <div>
                            <div class="font-medium">Нужен выезд и вывоз по Москве</div>
                            <div class="text-sm text-gray-500">
                                Менеджер уточнит объём, тип металла, удобное время и согласует условия вывоза.
                            </div>
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="[
                        cartStore.form.delivery_type === 'pickup_mo'
                            ? 'border-baseColor border-solid'
                            : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                    ]"
                >
                    <input
                        v-model="cartStore.form.delivery_type"
                        type="radio"
                        value="pickup_mo"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <div class="flex flex-col w-full gap-2">
                        <div class="flex justify-between">
                            <div>
                                <div class="font-medium">Нужен выезд и вывоз по Московской области</div>
                                <div class="text-sm text-gray-500">
                                    После заявки мы свяжемся с вами и уточним адрес, объём лома и возможность выезда.
                                </div>
                            </div>
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="[
                        cartStore.form.delivery_type === 'demontage_request'
                            ? 'border-baseColor border-solid'
                            : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                    ]"
                >
                    <input
                        v-model="cartStore.form.delivery_type"
                        type="radio"
                        value="demontage_request"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <div>
                        <div class="font-medium">Нужны демонтаж, погрузка или вывоз</div>
                        <div class="text-sm text-gray-500">
                            Оставьте заявку, если кроме приёма металлолома вам нужны дополнительные работы на объекте.
                        </div>
                    </div>
                </label>
            </div>
        </div>

        <div v-if="cartStore.form.delivery_type !== 'self_delivery'" class="mt-3">
            <h2 class="text-xl font-medium">Укажите адрес</h2>
            <div ref="dropdownRef" class="relative mb-4">
                <input
                    v-model="query"
                    @input="fetchSuggestions"
                    type="text"
                    placeholder="Адрес, откуда нужно забрать лом"
                    autocomplete="off"
                    class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"
                />
                <ul
                    v-if="suggestions.length"
                    class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto"
                >
                    <li
                        v-for="(suggestion, i) in suggestions"
                        :key="i"
                        @click="selectSuggestion(suggestion.label)"
                        class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm"
                    >
                        {{ suggestion.label }}
                    </li>
                </ul>
            </div>

            <p class="text-xs text-gray-500">
                Для точной оценки менеджеру могут понадобиться фото, описание лома, примерный вес и информация о подъезде.
            </p>
        </div>

        <div>
            <h2 class="text-xl font-medium mb-3">Как удобно получить оплату</h2>
            <div class="space-y-3">
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        v-model="cartStore.form.payment_type"
                        type="radio"
                        value="cash"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Наличными</span>
                </label>

                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        v-model="cartStore.form.payment_type"
                        type="radio"
                        value="card"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Переводом на карту</span>
                </label>

                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        v-model="cartStore.form.payment_type"
                        type="radio"
                        value="invoice"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Безналичный расчёт для юр. лиц</span>
                </label>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
input[type="radio"],
input[type="checkbox"] {
    accent-color: #ff9a00;
}
</style>
